// Context hooks - replacing Apollo
import { useEffect, useState } from "react";
import Box from "../../../components/Box/Box";
import Table from "../../../components/Table/Table";
import { utils } from "../../../helpers";
import { useResult } from "../../../shared/formContext/hooks/useResult";

// Types
import type { MonthlyData } from "./../interfaces/savingsCertificatesTypes";
import {SavingsCertificatesResult} from "./../interfaces/savingsCertificatesTypes";
import {__} from "@wordpress/i18n";

/**
 * UI message constants (Portuguese labels)
 */
const MESSAGES = {
  headerTitle: "Calendarização da subscrição",
  quarter: "Ano",
  startBalance: "Valor no início do ano",
  subscriptions: "Subscrições adicionais",
  netInterest: "Juros líquidos ao ano",
  endBalance: "Valor no final do ano",
  firstRowTitle: "Total",
};

/**
 * Generates table rows for each quarter with formatted values
 *
 * @param data - Raw quarterly data array
 * @returns Table-compatible row data with formatted currency values
 */
const generateSavingsTableData = (data: ScheduleData[]): any[] => {
  if (!data) return [];

  return data.map((item) => [
    {
      value: item.year,
      className: "text-left",
    },
    { value: utils.toEuro(item.initial_balance || 0) },
    { value: utils.toEuro(item.total_contributions || 0) },
    { value: utils.toEuro(item.interest_amount || 0) },
    { value: utils.toEuro(item.end_balance || 0) },
  ]);
};

/**
 * SavingsTable Component
 *
 * Renders a single-year savings table with quarterly breakdown and yearly totals
 * Uses collapsible Box component for better UX
 *
 * @param year - The year for this data group
 * @param total - Final accumulated total for the year
 * @param data - Quarterly entries for the year
 * @param isOpen - Whether the Box starts expanded
 */
const SavingsTable: React.FC<{
  data: ScheduleData[];
  isOpen: boolean;
}> = ({ data, isOpen }) => {
  // Table headers configuration
  const headers = [
    { value: MESSAGES.quarter, className: "text-left" },
    { value: MESSAGES.startBalance },
    { value: MESSAGES.subscriptions },
    { value: MESSAGES.netInterest },
    { value: MESSAGES.endBalance },
  ];

  // Calculate totals for the first row (yearly summary)
  const firstRow = [
    { value: MESSAGES.firstRowTitle },
    { value: "-" },
    {
      value: utils.toEuro(
        data.reduce((acc, item) => acc + (item.total_contributions || 0), 0),
      ),
    },
    {
      value: utils.toEuro(
        data.reduce((acc, item) => acc + (item.interest_amount || 0), 0),
      ),
    },
    {
      value: utils.toEuro(data[data.length - 1]?.end_balance || 0),
    },
  ];

  // Generate quarterly breakdown data
  const quarterlyData = generateSavingsTableData(data);

  return (
    <Box
      collapsible={{ open: isOpen }}
      noPadding
      header={{
        title: __("Resultados anuais", 'df-simulators')
      }}
    >
      <Table headers={headers} firstRow={firstRow} data={quarterlyData} />
    </Box>
  );
};

interface ScheduleData {
  year: string;
  initial_balance: number;
  total_contributions: number;
  interest_amount: number;
  end_balance: number;
}

/**
 * SavingsCertificatesFooter Component
 *
 * Main footer component for displaying the full investment schedule
 * Organizes quarterly data by year and renders collapsible tables
 * Uses Context API instead of Apollo for state management
 *
 * @returns JSX element containing the schedule tables or null if no data
 */
const SavingsCertificatesFooter: React.FC = () => {
  // Use Context hook instead of Apollo reactive variables
  const { resultData } = useResult();
  const { response } = resultData;

  // Local state for organized schedule data
  const [scheduleData, setScheduleData] = useState<ScheduleData[]>([]);

  const apiResult = response?.data.result as SavingsCertificatesResult;
  /**
   * Effect to process API response and organize data by year
   * Transforms flat quarterly array into year-grouped structure
   */
  useEffect(() => {
    if (!apiResult) return;

    const monthly: MonthlyData[] = (apiResult.monthly_breakdown ?? [])
      .filter(m => m.month) // skip month 0
      .sort((a, b) => a.month - b.month);

    // Precompute the last month of each year for fast lookups
    const lastOfYear = new Map<number, MonthlyData>();
    for (const m of monthly) {
      const y = Math.ceil(m.month / 12);
      lastOfYear.set(y, m); // sorted, so last write wins
    }

    const byYear = new Map<number, ScheduleData>();

    // Build yearly rollups with correct baselines
    monthly.forEach(m => {
      const y = Math.ceil(m.month / 12);

      let curr = byYear.get(y);
      if (!curr) {
        const prevYearLast = lastOfYear.get(y - 1);

        // Baselines from previous YEAR (not previous month)
        const initial_balance = prevYearLast ? prevYearLast.net_total : m.main_deposit;

        curr = {
          year: String(y),
          initial_balance,
          total_contributions: 0,
          interest_amount: 0,
          end_balance: 0,
        } as ScheduleData;

        byYear.set(y, curr);
      }

      // Contributions within the year = cumulative minus previous-year cumulative
      curr.total_contributions += m.contribution;

      // Sum interest for the year
      curr.interest_amount += m.interest_net;

      // Track latest month’s ending balance for the year
      curr.end_balance = m.net_total;
    });

    setScheduleData(
      Array.from(byYear.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, v]) => v)
    );
  }, [apiResult]);


  // Don't render if no schedule data is available
  if (Object.keys(scheduleData).length === 0) return null;

  return (
    <div className="savings-certificates-footer">
      <h3 className={"mt-12 mb-10"}>{MESSAGES.headerTitle}</h3>
      <SavingsTable
        data={scheduleData || []}
        isOpen={true}
      />
    </div>
  );
};

export default SavingsCertificatesFooter;
